import { motion } from 'framer-motion';
import { Mascot } from '@/components/Mascot';
import { usePreferredSport } from '@/hooks/usePreferredSport';

type SportKey = 'football' | 'basketball' | 'soccer' | 'baseball';
type FeedbackType = 'correct' | 'incorrect' | 'encouragement';

const SPORT_MESSAGES: Record<SportKey, Record<FeedbackType, string[]>> = {
  football: {
    correct: [
      "Great read! That's the right call.",
      "Locked in. Perfect answer.",
      "You read that defense like a pro.",
      "That's how you move the chains.",
      "Film study is paying off.",
    ],
    incorrect: [
      "Not quite — let's review that.",
      "Missed read. But great learning moment.",
      "The defense got you there. Let's study up.",
      "Off target — but every rep counts.",
    ],
    encouragement: [
      "You've got this — stay locked in.",
      "Keep grinding. The next rep is yours.",
    ],
  },
  basketball: {
    correct: [
      "Bucket! That's the right call.",
      "Smooth read — nothing but net.",
      "You saw the play develop. Pro-level IQ.",
      "Great court vision.",
      "Film study is paying off.",
    ],
    incorrect: [
      "Brick — but every miss teaches you something.",
      "Defense closed out on you. Let's review.",
      "Off the rim. Reset and run it back.",
      "Not the right read — let's break it down.",
    ],
    encouragement: [
      "Stay locked in — next possession is yours.",
      "Keep working. Reps build IQ.",
    ],
  },
  soccer: {
    correct: [
      "Top corner! Perfect read.",
      "Great vision on the pitch.",
      "You read the run like a veteran.",
      "Class touch — that's the right call.",
      "Film study is paying off.",
    ],
    incorrect: [
      "Off target — let's review that.",
      "The press caught you out. Let's study up.",
      "Not the right run — keep your head up.",
      "Missed it — but every rep sharpens your IQ.",
    ],
    encouragement: [
      "Keep your head up — next phase is yours.",
      "Stay locked in. The work shows up on match day.",
    ],
  },
  baseball: {
    correct: [
      "Crushed it! That's the right call.",
      "You read the pitch perfectly.",
      "Heads-up baseball — pro instincts.",
      "Smart play — that's how you win innings.",
      "Film study is paying off.",
    ],
    incorrect: [
      "Swing and a miss — let's review that.",
      "Got fooled on that one. Reset for the next pitch.",
      "Not quite — let's break down the situation.",
      "Caught looking. Every at-bat is a lesson.",
    ],
    encouragement: [
      "Step back in the box — next pitch is yours.",
      "Keep grinding. Reps build instincts.",
    ],
  },
};

function pickMessage(sport: string | undefined, type: FeedbackType): string {
  const key = (sport && (sport in SPORT_MESSAGES) ? sport : 'football') as SportKey;
  const pool = SPORT_MESSAGES[key][type];
  return pool[Math.floor(Math.random() * pool.length)];
}

interface CoachFeedbackProps {
  type: 'correct' | 'incorrect';
  explanation?: string;
}

export function CoachFeedback({ type, explanation }: CoachFeedbackProps) {
  const { preferredSport } = usePreferredSport();
  const message = pickMessage(preferredSport, type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className={`rounded-xl p-4 border-2 ${
        type === 'correct'
          ? 'bg-success/10 border-success/30'
          : 'bg-destructive/10 border-destructive/30'
      }`}
    >
      <div className="flex items-start gap-3">
        <Mascot
          mood={type === 'correct' ? 'correct' : 'idle'}
          sport={preferredSport}
          size={48}
          className="flex-shrink-0 -mt-1"
        />
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-lg mb-1">
            {message}
          </p>
          {explanation && (
            <p className="font-body text-sm text-muted-foreground leading-relaxed">
              {explanation}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
